class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();

    this.header = document.querySelector(".section-header");
    this.annoucementBar = document.querySelector(".announcement-bar-section");
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) => summary.addEventListener("click", this.onSummaryClick.bind(this)));
    this.querySelectorAll("button:not(.localization-selector)").forEach((button) => button.addEventListener("click", this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector("summary")) : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector("button"));
      summaryElement.nextElementSibling.removeEventListener("transitionend", addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();

      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia("(max-width: 989px)")) {
        document.documentElement.style.setProperty("--viewport-height", `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        parentMenuElement && parentMenuElement.classList.add("submenu-open");
        !reducedMotion || reducedMotion.matches ? addTrapFocus() : summaryElement.nextElementSibling.addEventListener("transitionend", addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    }, 25);

    summaryElement.setAttribute("aria-expanded", true);

    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);

    this.header.classList.add('reset-z-index');
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle.querySelectorAll(".submenu-open").forEach((submenu) => {
      submenu.classList.remove("submenu-open");
    });

    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    document.body.classList.remove(`overflow-hidden-undefined`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    this.header.classList.remove('reset-z-index');
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute("open") && !this.mainDetailsToggle.contains(document.activeElement)) this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    if (!detailsElement) return;

    const parentMenuElement = detailsElement.closest(".submenu-open");

    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");
    detailsElement.querySelector("summary").setAttribute("aria-expanded", false);
    removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(detailsElement.closest("details[open]"), detailsElement.querySelector("summary"));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define("menu-drawer", MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  onResize = () => {
    this.header && document.documentElement.style.setProperty("--header-bottom-position", `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`);
    document.documentElement.style.setProperty("--viewport-height", `${window.innerHeight}px`);
  };
}

customElements.define("mobile-menu-nav", HeaderDrawer);

class SliderComponent extends HTMLElement {
  constructor() {
    super();

    this.slider = this.classList.contains('slideshow') ? this : this.querySelector('[id^="Slider-"]');
    if (!this.slider) return;

    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.dots = this.querySelectorAll('.slider-dot');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.isInfinite = this.dataset.infinite === 'true';
    this.isDraggable = this.dataset.draggable === 'true';
    // Only set on markup rendered where every card is guaranteed visible (not inside a closed
    // drawer/modal) at construction time - e.g. a plain product grid. See rebuildSlider callers below.
    this.optimizeResize = this.dataset.optimizeResize === 'true';

    this.isAdjustingInfiniteScroll = false;
    this.mouseDown = false;
    this.noMovement = true;
    this.dragDirection = null;
    this.currentPosition = 1;
    this.dragDeltaX = 0;

    if (this.isInfinite) {
      // Some sliders (the PDP gallery) render their clones server-side to avoid layout shift; those
      // only need the count of trailing clones, which is one fewer than the total.
      const renderedClones = this.querySelectorAll('.slide--clone').length;
      renderedClones ? (this.cloneCount = renderedClones - 1) : this.cloneSlides();
    }
    this.initPages();

    if (this.isInfinite) {
      this.scrollToInitialPosition();
    }

    if (this.isDraggable) this.addDragListeners();

    // A single real slide can never become a viable carousel (checkCarouselViability always
    // disables prev/next, which don't even exist in the DOM for these), so no resize outcome
    // changes anything - skip the observer entirely rather than rebuilding for nothing.
    if (this.optimizeResize && this.getRealSlides().length <= 1) {
      // no-op: nothing to observe
    } else {
      // ResizeObserver always delivers one initial callback shortly after observe() - when
      // optimizeResize is set, initPages() above already measured this synchronously, so that
      // first callback is redundant and would otherwise rebuild every slider on the page twice.
      let skipNextResize = this.optimizeResize;

      const resizeObserver = new ResizeObserver(() => {
        if (skipNextResize) {
          skipNextResize = false;
          return;
        }

        this.rebuildSlider();
      });

      resizeObserver.observe(this.slider);
    }

    this.slider.addEventListener('scroll', this.onScroll.bind(this));
    this.slider.addEventListener('scrollend', this.onScrollEnd.bind(this));
    this.prevButton?.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton?.addEventListener('click', this.onButtonClick.bind(this));
    this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick.bind(this)));
  }

  getAllSlides() {
    return Array.from(this.slider.querySelectorAll('[id^="Slide-"]'));
  }

  getRealSlides() {
    return this.getAllSlides().filter((slide) => !slide.classList.contains('slide--clone'));
  }

  getCloneSlides() {
    return this.getAllSlides().filter((slide) => slide.classList.contains('slide--clone'));
  }

  rebuildSlider() {
    const previousPosition = this.currentPosition;

    if (this.isInfinite) {
      this.getCloneSlides().forEach((slide) => slide.remove());
      this.cloneSlides();
    }

    this.initPages();
    this.checkCarouselViability();

    if (this.isInfinite) {
      this.scrollToPosition(previousPosition);
    }
  }

  cloneSlides() {
    const realSlides = this.getRealSlides();
    if (realSlides.length <= 1) return;

    const visibleSlides = this.getVisibleSlidesCount(realSlides);
    this.cloneCount = Math.min(realSlides.length, Math.max(visibleSlides, 1));

    const prependClones = realSlides.slice(-this.cloneCount).map((slide) => this.createClone(slide));
    const appendClones = realSlides.slice(0, this.cloneCount).map((slide) => this.createClone(slide));

    prependClones.reverse().forEach((clone) => {
      this.slider.insertBefore(clone, this.slider.firstChild);
    });

    appendClones.forEach((clone) => {
      this.slider.appendChild(clone);
    });
  }

  createClone(slide) {
    const clone = slide.cloneNode(true);

    clone.classList.add('slide--clone');
    // Never let a clone carry the real edge slide's structural marker (see
    // markEdgeSlides) - cloneNode copies classList verbatim, and a clone of
    // the first/last real slide would otherwise wrongly inherit it.
    clone.classList.remove('slide--edge-first', 'slide--edge-last');
    clone.setAttribute('aria-hidden', 'true');
    // cloneNode copies the slide's own data-media-id verbatim, and clones
    // are prepended before the real slides in the DOM. Code outside this
    // component (media-gallery.js, product-modal.js, global.js) looks up
    // the active slide with querySelector('[data-media-id="..."]'), which
    // returns the first DOM match - without stripping this, thumbnail
    // clicks and variant changes resolve to a prepended clone instead of
    // the real slide.
    clone.removeAttribute('data-media-id');
    clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    clone.querySelectorAll('a, button, input, select, textarea').forEach((el) => {
      el.setAttribute('tabindex', '-1');
    });

    return clone;
  }

  // CSS can't reliably use :first-child/:last-child to style the real edge
  // slides once clones are prepended/appended - the DOM's structural
  // first-child/last-child become clones instead. This marks the actual
  // first/last real slide with a class CSS can target unambiguously.
  markEdgeSlides() {
    const realSlides = this.getRealSlides();
    realSlides.forEach((slide, index) => {
      slide.classList.toggle('slide--edge-first', index === 0);
      slide.classList.toggle('slide--edge-last', index === realSlides.length - 1);
    });
  }

  getVisibleSlidesCount(slides = this.getRealSlides()) {
    if (slides.length < 2) return 1;

    const first = slides[0];
    const second = slides[1];
    const offset = second.offsetLeft - first.offsetLeft;

    if (!offset) return 1;

    // Math.ceil, not Math.round - this feeds cloneCount, which has to fully
    // cover the viewport width during the wrap transition. On a genuinely
    // fractional layout (e.g. this carousel's 2.5-slides-visible design),
    // the true ratio sits right on a rounding knife-edge - a sub-pixel
    // measurement wobble (spacing subtraction, zoom, border-box rounding)
    // can tip it to 2.49 instead of 2.5, and Math.round would floor that to
    // 2 clones - one short of what a 2.5-wide viewport needs, leaving a gap
    // during the wrap. Rounding up always errs toward "one clone too many"
    // rather than "one clone short".
    return Math.max(1, Math.ceil(this.slider.clientWidth / offset));
  }

  getMaxPosition() {
    if (!this.realSlides?.length || !this.slidesPerPage) return 1;
    return Math.max(1, this.realSlides.length - this.slidesPerPage + 1);
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');

    if (this.isInfinite) this.cloneSlides();
  }

  scrollToInitialPosition() {
    this.scrollToPosition(1);
  }

  scrollToPosition(position) {
    const realSlides = this.getRealSlides();
    if (realSlides.length === 0) return;

    const targetIndex = Math.max(0, Math.min(position - 1, realSlides.length - 1));

    // Instant, not 'auto' - the slider's CSS sets scroll-behavior: smooth, and
    // 'auto' defers to that CSS value rather than snapping immediately.
    this.slider.scrollTo({
      left: realSlides[targetIndex].offsetLeft,
      behavior: 'instant'
    });

    this.updateCurrentPosition();
  }

  updateCurrentPosition() {
    if (!this.sliderItemOffset || !this.realSlides) return;

    const scrollLeft = this.slider.scrollLeft;
    const realSlides = this.getRealSlides();
    
    // Find which real slide is currently visible
    let closestSlide = realSlides[0];
    let closestDistance = Math.abs(closestSlide.offsetLeft - scrollLeft);

    for (let slide of realSlides) {
      const distance = Math.abs(slide.offsetLeft - scrollLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestSlide = slide;
      }
    }

    this.currentPosition = realSlides.indexOf(closestSlide) + 1;
    this.renderUI();
  }

  initPages() {
    this.sliderItems = this.getAllSlides();
    this.realSlides = this.getRealSlides();
    this.markEdgeSlides();
    this.sliderItemsToShow = this.realSlides.filter((element) => element.clientWidth > 0);

    if (this.sliderItemsToShow.length === 0) return;

    if (this.sliderItemsToShow.length === 1) {
      this.sliderItemOffset = this.sliderItemsToShow[0].clientWidth;
      this.slidesPerPage = 1;
      this.maxPosition = 1;
      this.totalPages = 1;
      this.currentPosition = 1;
      this.renderUI();
      return;
    }

    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.max(
      1,
      Math.round(this.slider.clientWidth / this.sliderItemOffset)
    );

    this.maxPosition = this.getMaxPosition();
    this.totalPages = this.maxPosition;
    
    if (!this.isInfinite) {
      this.currentPosition = Math.max(1, Math.min(this.currentPosition || 1, this.maxPosition));
    }

    this.renderUI();
  }

  checkCarouselViability() {
    const nonClonedSlides = this.getRealSlides();
    const isViable = nonClonedSlides.length > this.slidesPerPage;

    this.prevButton?.classList.toggle('disabled', !isViable);
    this.nextButton?.classList.toggle('disabled', !isViable);
  }

  onScroll() {
    if (this.isAdjustingInfiniteScroll || !this.isInfinite || this.isDragging) return;

    // 'scroll' fires repeatedly during the animation, so this is the
    // reliable place to catch the boundary crossing - relying on
    // 'scrollend' alone risks never firing in some cases, which would
    // strand the real scroll position inside the clone zone forever
    // (looking fine visually but desyncing every drag/position check after).
    if (this.checkInfiniteBoundary()) return;

    this.updateCurrentPosition();
  }

  // Supplementary safety net for the same check, run once scrolling has
  // fully settled - covers any edge case where 'scroll' events were too
  // sparse to catch the crossing mid-animation. checkInfiniteBoundary() is
  // a no-op if onScroll already corrected it.
  onScrollEnd() {
    if (this.isAdjustingInfiniteScroll || !this.isInfinite || this.isDragging) return;

    this.checkInfiniteBoundary();
  }

  // Returns true (and jumps back to the corresponding real slide) if
  // scrollLeft has crossed into the cloned slides at either end.
  checkInfiniteBoundary() {
    const realSlides = this.getRealSlides();
    if (realSlides.length === 0 || !this.sliderItemOffset) return false;

    const scrollLeft = this.slider.scrollLeft;
    const firstRealSlide = realSlides[0];
    const lastRealSlide = realSlides[realSlides.length - 1];

    // offsetLeft is always rounded to a whole pixel, but sliderItemOffset is
    // derived from a subtraction of two such rounded values - on slide
    // widths that aren't a round percentage (e.g. calc(40% - ...)), the
    // computed clone boundary can be off by a pixel or two from where the
    // clone's own offsetLeft actually lands. Without slack, a smooth-scroll
    // animation that settles just short of the exact threshold never trips
    // this check, so the view visibly rests on the clone forever instead of
    // getting the invisible snap back to the real slide.
    const boundaryTolerance = 2;

    // Landed in the cloned slides at the end (wrapped forward) - jump back
    // to the real first slide, which renders identically to the clone.
    if (scrollLeft >= lastRealSlide.offsetLeft + this.sliderItemOffset - boundaryTolerance) {
      this.isAdjustingInfiniteScroll = true;
      this.slider.scrollTo({
        left: firstRealSlide.offsetLeft,
        behavior: 'instant'
      });
      this.isAdjustingInfiniteScroll = false;
      this.currentPosition = 1;
      return true;
    }

    // Landed in the cloned slides at the beginning (wrapped backward) - jump
    // back to the real last slide, which renders identically to the clone.
    if (scrollLeft <= firstRealSlide.offsetLeft - this.sliderItemOffset + boundaryTolerance) {
      this.isAdjustingInfiniteScroll = true;
      this.slider.scrollTo({
        left: lastRealSlide.offsetLeft,
        behavior: 'instant'
      });
      this.isAdjustingInfiniteScroll = false;
      this.currentPosition = realSlides.length;
      return true;
    }

    return false;
  }

  renderUI() {
    if (this.currentPageElement) {
      this.currentPageElement.textContent = this.currentPosition;
    }

    if (this.pageTotalElement) {
      this.pageTotalElement.textContent = this.maxPosition;
    }

    this.updateDots();

    if (!this.isInfinite) {
      if (this.currentPosition <= 1) {
        this.prevButton?.setAttribute('disabled', 'disabled');
      } else {
        this.prevButton?.removeAttribute('disabled');
      }

      if (this.currentPosition >= this.maxPosition) {
        this.nextButton?.setAttribute('disabled', 'disabled');
      } else {
        this.nextButton?.removeAttribute('disabled');
      }
    }
  }

  updateDots() {
    this.dots.forEach((dot, index) => {
      dot.classList.toggle('slider-dot--active', index === this.currentPosition - 1);
    });
  }

  onButtonClick(event) {
    if (event.preventDefault) event.preventDefault();

    if (this.maxPosition <= 1) return;

    this.goToSlide(event.currentTarget.name);
  }

  // Single authoritative "move one slide" implementation, shared by the
  // prev/next buttons and by drag release - direction is 'next' or
  // 'previous'. Keeping this as one code path is what makes drag and
  // buttons behave identically instead of drifting apart.
  goToSlide(direction) {
    const realSlides = this.getRealSlides();
    if (!realSlides || realSlides.length === 0 || !this.sliderItemOffset) return;

    const currentIndex = this.currentPosition - 1;
    let targetIndex = currentIndex;

    if (direction === 'next') {
      targetIndex = (currentIndex + 1) % realSlides.length;
    } else if (direction === 'previous') {
      targetIndex = (currentIndex - 1 + realSlides.length) % realSlides.length;
    }

    // For infinite scroll, use smooth scroll to cloned slides which triggers auto-wrap
    if (this.isInfinite) {
      const isWrappingForward = direction === 'next' && currentIndex === realSlides.length - 1;
      const isWrappingBackward = direction === 'previous' && currentIndex === 0;

      if (isWrappingForward) {
        // Scroll forward to the cloned first slide at the end
        // onScroll will detect we've passed the boundary and jump back seamlessly
        const lastRealSlide = realSlides[realSlides.length - 1];
        const clonedFirstSlidePosition = lastRealSlide.offsetLeft + this.sliderItemOffset;

        this.slider.scrollTo({
          left: clonedFirstSlidePosition,
          behavior: 'smooth'
        });
        this.currentPosition = 1;
        this.renderUI();
        return;
      }

      if (isWrappingBackward) {
        // Scroll backward to the cloned last slide at the beginning
        // onScroll will detect we've passed the boundary and jump back seamlessly
        const firstRealSlide = realSlides[0];
        const clonedLastSlidePosition = firstRealSlide.offsetLeft - this.sliderItemOffset;

        this.slider.scrollTo({
          left: clonedLastSlidePosition,
          behavior: 'smooth'
        });
        this.currentPosition = realSlides.length;
        this.renderUI();
        return;
      }
    }

    // Normal scroll to target slide
    const targetSlide = realSlides[targetIndex];
    this.currentPosition = targetIndex + 1;
    this.slider.scrollTo({
      left: targetSlide.offsetLeft,
      behavior: 'smooth'
    });
    this.renderUI();
  }

  onDotClick(event) {
    const dotIndex = parseInt(event.currentTarget.dataset.index, 10);
    if (!dotIndex || dotIndex < 1) return;

    const realSlides = this.getRealSlides();
    if (dotIndex > realSlides.length) return;

    const targetSlide = realSlides[dotIndex - 1];
    this.slider.scrollTo({
      left: targetSlide.offsetLeft,
      behavior: 'smooth'
    });
  }

  connectedCallback() {
    if (this.dataset.reset_scroll === 'true') {
      requestAnimationFrame(() => {
        if (this.isInfinite) {
          this.scrollToInitialPosition();
        } else {
          const firstSlide = this.realSlides?.[0];
          if (firstSlide) {
            this.slider.scrollTo({
              left: firstSlide.offsetLeft,
              behavior: 'instant'
            });
          }
        }
      }); 
    }
    
    if (this.dataset.reset_scroll === "true" && !this.closest(".product-grid")) {
      this.isInfinite ? this.slider.scrollLeft = this.sliderItems[1].offsetLeft : this.slider.scrollLeft = 0;
    }
  }

  addDragListeners() {
    if (this.getRealSlides().length <= 1) return;

    this.slider.addEventListener('mousedown', (event) => this.initDrag(event));
    this.slider.addEventListener('mousemove', (event) => this.onDrag(event));
    this.slider.addEventListener('mouseup', () => this.endDrag());
    this.slider.addEventListener('mouseleave', () => this.endDrag());

    this.slider.addEventListener('touchstart', (event) => this.initDrag(event), { passive: true });
    this.slider.addEventListener('touchmove', (event) => this.onDrag(event), { passive: false });
    this.slider.addEventListener('touchend', () => this.endDrag());
    this.slider.addEventListener('touchcancel', () => this.endDrag());

    // Prevent link/button clicks immediately after dragging
    this.slider.addEventListener('click', (event) => {
      if (this.wasDragging) {
        const target = event.target.closest('a, button');
        if (target) {
          event.preventDefault();
          event.stopPropagation();
        }
      }
    }, true); // Use capture phase to catch clicks before they propagate
  }

  initDrag(event) {
    if (event.type !== 'touchstart') event.preventDefault();

    const pageX = event.pageX !== undefined ? event.pageX : event.targetTouches[0].pageX;
    const pageY = event.pageY !== undefined ? event.pageY : event.targetTouches[0].pageY;

    this.modalTrigger = event.target;
    this.slider.style.cursor = 'grabbing';
    this.slider.classList.add('is-dragging');
    this.mouseDown = true;
    this.isDragging = true;
    this.startX = pageX;
    this.startY = pageY;
    this.scrollStart = this.slider.scrollLeft;
    this.dragDirection = null;
    this.noMovement = true;
    this.hasDragged = false;
  }

  onDrag(event) {
    if (!this.mouseDown) return;

    const threshold = 50;
    const isTouch = event.type.startsWith('touch');
    const pageX = event.pageX !== undefined ? event.pageX : event.touches[0].pageX;
    const pageY = event.pageY !== undefined ? event.pageY : event.touches[0].pageY;
    const walk = pageX - this.startX;
    let noMovement = Math.abs(walk) < threshold;

    // Track the drag delta for snap direction
    this.dragDeltaX = walk;

    this.slider.scrollLeft = this.scrollStart - walk;

    if (this.dragDirection === null && isTouch) {
      const dx = Math.abs(pageX - this.startX);
      const dy = Math.abs(pageY - this.startY);
      this.dragDirection = dx > dy ? 'horizontal' : 'vertical';
    }

    if (this.dragDirection === 'vertical' && isTouch) noMovement = true;
    if (this.dragDirection === 'horizontal' || !isTouch) event.preventDefault();

    // Only set pointer-events: none if drag threshold is passed
    if (!noMovement && !this.hasDragged) {
      this.setLinkAction('none');
      this.hasDragged = true;
    }

    if (!noMovement) {
      this.noMovement = false;
    }
  }

  endDrag() {
    // mouseleave/mouseup/touchend fire even when no drag was ever started
    // (e.g. the cursor simply entering and leaving a card) - without this
    // guard, every such event ran the snap-to-slide logic below and nudged
    // the carousel backward one slide each time.
    if (!this.mouseDown) return;

    // Capture before it gets reset below - needed to gate the direction bias further down.
    const hadMovement = this.noMovement === false;

    if (this.noMovement !== false && this.mouseDown && this.dragDirection === null) {
      if (this.modalTrigger.classList.contains('product__media-toggle')) this.modalTrigger?.click();
    }

    this.mouseDown = false;
    this.isDragging = false;
    this.slider.style.cursor = 'auto';
    this.slider.classList.remove('is-dragging');
    this.setLinkAction('auto');
    this.noMovement = true;
    this.dragDirection = null;
    this.hasDragged = false;

    // Prevent link clicks for a brief moment after dragging ends
    if (hadMovement) {
      this.wasDragging = true;
      clearTimeout(this.wasDraggingTimeout);
      this.wasDraggingTimeout = setTimeout(() => {
        this.wasDragging = false;
      }, 100);
    }

    if (!this.sliderItemOffset) return;

    const dragWasLeft = this.dragDeltaX < 0; // Dragging left = moving forward/next
    this.dragDeltaX = 0;

    if (!hadMovement) {
      // A click, or a drag that never cleared the movement threshold - settle
      // back on the slide we're already meant to be on rather than nudging
      // to an adjacent one. Also self-corrects the scroll position if it
      // ever drifted slightly during the aborted drag.
      const realSlides = this.getRealSlides();
      if (realSlides.length === 0) return;

      const currentIndex = Math.max(0, Math.min(this.currentPosition - 1, realSlides.length - 1));
      this.slider.scrollTo({
        left: realSlides[currentIndex].offsetLeft,
        behavior: 'smooth'
      });
      return;
    }

    // A raw drag isn't clamped to "one slide's worth" - it can carry
    // scrollLeft straight past the clone boundary before release. If that
    // already happened, jump to the real slide now instead of letting
    // goToSlide's catch-up animation scroll backward from the overshoot to
    // the exact clone position first - that backward spring was the visible
    // "scroll from last to first" that buttons never show, since a button
    // click always starts from a clean anchor and never overshoots.
    if (this.isInfinite && this.checkInfiniteBoundary()) return;

    // Otherwise this is a normal in-bounds release - a real swipe always
    // advances exactly one slide, wrapping via the clones when at either
    // end, identical to a button click. Routing drag through the same
    // goToSlide() the buttons use (rather than a separate "how far did you
    // drag" heuristic) is what makes the two behave consistently.
    this.goToSlide(dragWasLeft ? 'next' : 'previous');
  }

  setLinkAction(pointer) {
    this.getAllSlides().forEach((slide) => {
      const actions = slide.querySelectorAll('button, a');

      actions.forEach((action) => {
        action.style.pointerEvents = pointer;
      });
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector(".slider-buttons");
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.querySelector(".slideshow__slide");

    if (this.sliderItemsToShow.length > 0) this.currentPosition = 1;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll(".slider-counter__link"));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener("click", this.linkToSlide.bind(this)));
    this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.getAttribute("data-autoplay") === "true") this.setAutoPlay();
  }

  setAutoPlay() {
    this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
    this.autoplaySpeed = this.dataset.speed * 1000;

    this.sliderAutoplayButton?.addEventListener("click", this.autoPlayToggle.bind(this));
    this.addEventListener("mouseover", this.focusInHandling.bind(this));
    this.addEventListener("mouseleave", this.focusOutHandling.bind(this));
    this.addEventListener("focusin", this.focusInHandling.bind(this));
    this.addEventListener("focusout", this.focusOutHandling.bind(this));

    this.play();
    this.autoplayButtonIsSetToPlay = true;
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll(".slider-counter__link");
    this.prevButton?.removeAttribute("disabled");

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove("slider-counter__link--active");
      link.removeAttribute("aria-current");
    });

    this.sliderControlButtons[this.currentPosition - 1]?.classList.add("slider-counter__link--active");
    this.sliderControlButtons[this.currentPosition - 1]?.setAttribute("aria-current", true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    const focusedOnAutoplayButton = event.target === this.sliderAutoplayButton || this.sliderAutoplayButton?.contains(event.target);
    if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
    this.play();
  }

  focusInHandling(event) {
    const focusedOnAutoplayButton = event.target === this.sliderAutoplayButton || this.sliderAutoplayButton?.contains(event.target);
    if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
      this.play();
    } else if (this.autoplayButtonIsSetToPlay) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute("aria-live", "off");

    clearInterval(this.autoplay);

    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute("aria-live", "polite");
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton?.classList.add("slideshow__autoplay--paused");
      this.sliderAutoplayButton?.setAttribute("aria-label", window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton?.classList.remove("slideshow__autoplay--paused");
      this.sliderAutoplayButton?.setAttribute("aria-label", window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const realSlides = this.getRealSlides();
    if (realSlides.length === 0) return;

    const currentIndex = this.currentPosition - 1;
    const nextIndex = (currentIndex + 1) % realSlides.length;
    const targetSlide = realSlides[nextIndex];

    this.slider.scrollTo({ 
      left: targetSlide.offsetLeft,
      behavior: 'smooth'
    });
  }

  setSlideVisibility() {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll("a");
      if (index === this.currentPosition - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute("tabindex");
          });
        item.setAttribute("aria-hidden", "false");
        item.removeAttribute("tabindex");
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute("tabindex", "-1");
          });
        item.setAttribute("aria-hidden", "true");
        item.setAttribute("tabindex", "-1");
      }
    });
  }

  linkToSlide(event) {
    event.preventDefault();

    const slideScrollPosition = this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);

    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define("slideshow-component", SlideshowComponent);

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);

      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((text) => {
          const html = document.createElement("div");
          html.innerHTML = text;
          const recommendations = html.querySelector("product-recommendations");

          if (recommendations && recommendations.innerHTML.trim().length) {
            this.innerHTML = recommendations.innerHTML;
          }

          if (!this.querySelector("slideshow-component") && this.classList.contains("complementary-products")) {
            this.remove();
          }

          // Number of cards loaded will be cards.length
          const cards = html.querySelectorAll(".grid__item");

          // When the section is re-loaded with products, liquid within cannot reach the outer feature wrap element
          // Need to add fit-exactly classes with js depending on how many products are loaded
          // With the extra class a width for showing an extra quarter or half overrides the exact fit width
          const feature_card_enabled = this.dataset.feature_card_enabled === "true";
          if (feature_card_enabled) {
            const columns_desktop = parseInt(this.dataset.columns_desktop);
            const cards_fit_desktop = cards.length <= columns_desktop;
            const feature_wrap_el = document.querySelector(`#shopify-section-${this.dataset.section_id} .carousel__feature-wrap`);
            if (cards_fit_desktop) {
              feature_wrap_el.classList.add("carousel__feature-wrap--cards-fit-desktop");
            }
          }

          const parent_section = document.getElementById(this.dataset.section_id);

          // Need to check recently tab before hiding the whole thing
          const hide_recently_tab = this.dataset.hide_recently_tab === "true";
          const product_handle = this.dataset.product_handle;
          const product_handles_list = localStorage.getItem("shopify_recently_viewed");
          const no_recently_viewed = !product_handles_list || product_handles_list == "" || product_handles_list == product_handle;

          if (cards) {
            // Loaded
            this.classList.add("product-recommendations--loaded");
          } else if (hide_recently_tab || no_recently_viewed) {
            // No items - recently not showing - hide the carousel
            if (parent_section) {
              parent_section.classList.add("hidden");
            }
          } else {
            // hide just product recommendations
            // hide both tabs
            const recommendations = parent_section.querySelector("product-recommendations");
            const recently = parent_section.querySelector("recently-viewed");
            const recommendations_tab = parent_section.querySelector(".product-recommendations-tab");
            const recently_tab = parent_section.querySelector(".recently-viewed-tab");

            recommendations_tab.classList.add("hidden");
            recently_tab.classList.add("hidden");
            recommendations.classList.remove("active");
            recently.classList.add("active");
          }
        })
        .catch((e) => {
          console.error(e);
        });
    };

    new IntersectionObserver(handleIntersection.bind(this), {
      rootMargin: "0px 0px 400px 0px",
    }).observe(this);
  }
}

customElements.define("product-recommendations", ProductRecommendations);

class UpsellProductRecommendations extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector(`#${this.id}`);

        if (recommendations) {
          if (recommendations.innerHTML.trim().length == 0) {
            fetch(this.dataset.fallbackurl)
              .then((response) => response.text())
              .then((text) => {
                html.innerHTML = text;
                const recommendations = html.querySelector(`#${this.id}`);
                this.innerHTML = recommendations.innerHTML;
                if (!this.querySelector("slideshow-component") && this.classList.contains("complementary-products")) {
                  this.remove();
                }
                if (html.querySelector(".grid__item")) {
                  this.classList.add("product-recommendations--loaded");
                }
              });
          } else {
            this.innerHTML = recommendations.innerHTML;
            if (!this.querySelector("slideshow-component") && this.classList.contains("complementary-products")) {
              this.remove();
            }
            if (html.querySelector(".grid__item")) {
              this.classList.add("product-recommendations--loaded");
            }
          }
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define("upsell-product-recommendations", UpsellProductRecommendations);

class CrossellProductRecommendations extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("crossell-product-recommendations");

        if (recommendations) {
          if (recommendations.innerHTML.trim().length === 0) {
            fetch(this.dataset.fallbackurl)
              .then((response) => response.text())
              .then((text) => {
                html.innerHTML = text;
                const recommendations = html.querySelector("crossell-product-recommendations");

                this.innerHTML = recommendations.innerHTML;
                if (!this.querySelector("slideshow-component") && this.classList.contains("complementary-products")) {
                  this.remove();
                }
                if (html.querySelector(".grid__item")) {
                  this.classList.add("product-recommendations--loaded");
                }
              });
          } else {
            this.innerHTML = recommendations.innerHTML;
            if (!this.querySelector("slideshow-component") && this.classList.contains("complementary-products")) {
              this.remove();
            }
            if (html.querySelector(".grid__item")) {
              this.classList.add("product-recommendations--loaded");
            }
          }
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define("crossell-product-recommendations", CrossellProductRecommendations);

// class ShopTheLook extends HTMLElement {
//   constructor() {
//     super();
//   }

//   connectedCallback() {
//     var shopLookGrid = this.querySelector(".shop-the-look-product-grid");
//     var shopLookGridChildren = shopLookGrid?.querySelectorAll(".grid__item");

//     var shopLookSlider = this.querySelector("shop-look ul.slider");
//     var shopLookSliderChildren = shopLookSlider?.querySelectorAll(".grid__item.slider__slide");

//     if (shopLookGridChildren?.length > 0) {
//       this.closest(".shop-the-look-container")?.classList.remove("hidden");
//     } else {
//       this.closest(".shop-the-look-container")?.classList.add("hidden");
//     }

//     if (shopLookSliderChildren?.length > 0 || Shopify.designMode) {
//       this.closest(".carousel")?.classList.remove("hidden");
//     } else {
//       this.closest(".carousel")?.classList.add("hidden");
//     }
//   }
// }

// customElements.define("shop-look", ShopTheLook);

class RecentlyViewed extends HTMLElement {
  /*
    data-full_width="{{ full_width }}"
    data-max_cards_to_output="{{ max_cards_to_output }}"
    data-sectionid="{{ section.id }}"
    data-columns_desktop="{{ columns_desktop }}"
    data-columns_mobile="{{ columns_mobile }}"
    data-feature_card_enabled="{{ section.settings.feature_card_enabled }}"
    data-recently_is_only_tab="{{ recently_is_only_tab }}"
    data-on_product_page="{{ on_product_page }}"
  */

  constructor() {
    super();
  }

  config = {
    maxToShow: parseInt(this.dataset.max_cards_to_output),
    minToShow: 1,
    howManyToStoreInMemory: parseInt(this.dataset.max_cards_to_output),
    numberProducts: 0,
    onComplete: null,
  };
  productHandleQueue = [];

  cookie = {
    name: "shopify_recently_viewed",
    write: function (recentlyViewed) {
      localStorage.setItem(this.name, recentlyViewed.join(","));
    },
    read: function () {
      let recentlyViewed = [];
      let cookieValue = localStorage.getItem(this.name);
      if (cookieValue) {
        recentlyViewed = cookieValue.split(",");
      }
      return recentlyViewed;
    },
    destroy: function () {
      localStorage.removeItem(this.name);
    },
    remove: function (productHandle) {
      let recentlyViewed = this.read();
      let position = recentlyViewed.indexOf(productHandle);
      if (position !== -1) {
        recentlyViewed.splice(position, 1);
        this.write(recentlyViewed);
      }
    },
  };

  finalize = () => {
    // Add and remove classes that depend on the number of cards

    // Get elements
    const sliderComponent = this.querySelector("slider-component");
    const sliderUL = this.querySelector("slider-component ul.slider");
    const featureWrap = document.querySelector(`#shopify-section-${this.dataset.section_id}.carousel__feature-wrap`);
    const max_cards_to_output = this.config.maxToShow;

    // Hide buttons if cards fit
    const columns_desktop = parseInt(this.dataset.columns_desktop);
    const columns_mobile = parseInt(this.dataset.columns_mobile);
    let cards_fit_desktop = sliderUL.children.length <= columns_desktop;
    const cards_fit_mobile = sliderUL.children.length <= columns_mobile;
    const feature_card_enabled = this.dataset.feature_card_enabled === "true";
    if (feature_card_enabled) {
      cards_fit_desktop = sliderUL.children.length < columns_desktop;
    } else {
      cards_fit_desktop = sliderUL.children.length <= columns_desktop;
    }

    if (cards_fit_desktop) {
      sliderComponent.classList.add("slider-component--cards-fit-desktop");
    } else {
      sliderComponent.classList.remove("slider-component--cards-fit-desktop");
    }

    if (cards_fit_mobile) {
      sliderComponent.classList.add("slider-component--cards-fit-mobile");
    } else {
      sliderComponent.classList.remove("slider-component--cards-fit-mobile");
    }

    if (featureWrap) {
      if (cards_fit_desktop) {
        featureWrap.classList.add("carousel__feature-wrap--cards-fit-desktop");
      } else {
        featureWrap.classList.remove("carousel__feature-wrap--cards-fit-desktop");
      }
    }

    if (this.dataset.full_width === "true") {
      // set full_width class on parent
      sliderComponent.classList.add("slider-component-full-width");
      sliderComponent.classList.remove("page-width");

      // Add dummy list items for padding

      /*
      const li = document.createElement('li');
      li.setAttribute('aria-hidden', 'true');
      li.setAttribute('class', 'slider__slide first-dummy');
      li.innerHTML = '<!--fake padding-->';

      const li_2 = document.createElement('li');
      li_2.setAttribute('aria-hidden', 'true');
      li_2.setAttribute('class', 'slider__slide last-dummy');
      li_2.innerHTML = '<!--fake padding-->';
      */

      sliderUL.prepend(li);
      sliderUL.appendChild(li_2);
    } else {
      sliderComponent.classList.remove("slider-component-full-width");
      sliderComponent.classList.add("page-width");
    }

    sliderUL.scrollLeft = 0;
    this.classList.add("recently-viewed--loaded");
  };

  getRecentProducts = async () => {
    let searchRecentlyUrl;
    let productHandle;

    if (window.location.pathname.indexOf("/products/") !== -1) {
      productHandle = window.location.pathname.match(/\/products\/([a-z0-9\-]+)/)[1];
      this.productHandleQueue = this.productHandleQueue.filter((handle) => productHandle !== handle);
    }

    if (this.productHandleQueue.length < this.config.minToShow) {
      return;
    }
    this.config.numberProducts = this.productHandleQueue.length;

    this.productHandleQueue.length = this.config.maxToShow;
    searchRecentlyUrl = "/search?view=ua_recently-viewed&q=" + this.productHandleQueue.toString();
    await fetch(searchRecentlyUrl)
      .then((response) => response.text())
      .then((html) => {
        document.querySelector(`#shopify-section-${this.dataset.sectionid} recently-viewed`).innerHTML = html;

        this.finalize();

        const title = document.querySelector("recently-viewed .carousel__title");

        title.innerHTML = this.dataset.title;
      })
      .catch((err) => {
        console.warn("Something went wrong.", err);
      });
  };

  recordRecentlyViewed = (values) => {
    let vals = values || {};
    let recentlyViewed = this.cookie.read();
    let productHandle;
    let position;

    // Update defaults.
    this.config = { ...this.config, ...vals };

    // If we are on a product page.
    if (window.location.pathname.indexOf("/products/") !== -1) {
      productHandle = window.location.pathname.match(/\/products\/([a-z0-9\-]+)/)[1];

      position = recentlyViewed.indexOf(productHandle);

      if (position === -1) {
        recentlyViewed.unshift(productHandle);

        recentlyViewed = recentlyViewed.splice(0, this.config.howManyToStoreInMemory);
      } else {
        recentlyViewed.splice(position, 1);
        recentlyViewed.unshift(productHandle);
      }

      this.cookie.write(recentlyViewed);
    }
  };

  showRecentlyViewed = (parameters) => {
    // Hide whole carousel if only tab and nothing to show
    const recently_is_only_tab = this.dataset.recently_is_only_tab === "true";
    const sectionid = this.dataset.section_id;
    const carousel = document.querySelector(".carousel.section-" + sectionid);
    const product_handles_list = localStorage.getItem("shopify_recently_viewed");
    if (this.dataset.on_product_page === "true") {
      const product_handle = this.dataset.product_handle;
      const no_recently_viewed = !product_handles_list || product_handles_list == "" || product_handles_list == product_handle;
      if (recently_is_only_tab && no_recently_viewed && carousel) {
        carousel.classList.add("hidden");
      } else {
        const no_recently_viewed = !product_handles_list || product_handles_list == "";
        if (recently_is_only_tab && no_recently_viewed && carousel) {
          carousel.classList.add("hidden");
        }
      }
    }

    let params = parameters || {};
    // Update defaults.
    this.config = { ...this.config, ...params };

    // Read cookie.
    this.productHandleQueue = this.cookie.read();

    if (this.productHandleQueue === null || this.productHandleQueue === undefined) {
      return false;
    }

    this.config.maxToShow = Math.min(this.productHandleQueue.length, this.config.maxToShow);

    if (this.config.maxToShow) {
      this.getRecentProducts().then();
    }
  };

  connectedCallback() {
    this.showRecentlyViewed();
    this.recordRecentlyViewed();
  }
}

customElements.define("recently-viewed", RecentlyViewed);

class CopyToClipboard extends HTMLElement {
  constructor() {
    super();
    this.textContainer = this.querySelector("span");
    this.clipboadText = this.dataset.clipboardText;
    this.copiedMessage = this.dataset.copiedMessage;
    this.addEventListener("click", () => {
      navigator.clipboard.writeText(this.clipboadText).then(() => {
        this.textContainer.innerHTML = this.copiedMessage;
      });
    });
  }
}

customElements.define("copy-to-clipboard", CopyToClipboard);